import { useMemo } from 'react'
import { faCheck } from '@fortawesome/pro-duotone-svg-icons'

import Icon from '../icon/Icon'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import { MovePrepTask } from '../../_types/move-prep'
import { buildCategoryGroups, computeOverallPercent } from './helpers'
import {
  Section,
  Header,
  HeaderTopRow,
  Title,
  PercentLabel,
  ProgressTrack,
  ProgressFill,
  Subtitle,
  CategoryList,
  CategoryBlock,
  CategorySummary,
  CategoryGlyph,
  CategoryBody,
  CategoryTitle,
  CategoryHint,
  CategoryProgress,
  TaskList,
  TaskRow,
  TaskCheckbox,
  TaskBody,
  TaskTitle,
  TaskHint,
} from './styled'
import { MovePrepChecklistProps } from './types'

/**
 * Move-prep checklist surface. 24 pre-defined renter tasks across 5
 * categories, each individually checkable. Progress persists to
 * `profile.movePrepCompletions` so progress survives reloads + syncs
 * across devices.
 *
 * @param {MovePrepChecklistProps} props
 * @returns {JSX.Element | null}
 */
const MovePrepChecklist = ({
  shouldShow = true,
}: MovePrepChecklistProps): JSX.Element | null => {
  const { profile, patchProfile } = useQuickPassProfile()

  const completions = useMemo<Record<string, number>>(
    () => profile?.movePrepCompletions ?? {},
    [profile],
  )

  const groups = useMemo(() => buildCategoryGroups(completions), [completions])

  const overallPercent = useMemo(
    () => computeOverallPercent(completions),
    [completions],
  )

  if (shouldShow === false) {
    return null
  }

  if (profile === null) {
    return null
  }

  const handleToggle = (task: MovePrepTask): void => {
    const isCurrentlyChecked = (completions[task.id] ?? 0) > 0
    const next: Record<string, number> = { ...completions }

    if (isCurrentlyChecked === true) {
      delete next[task.id]
    } else {
      next[task.id] = Date.now()
    }

    void patchProfile({ movePrepCompletions: next })
  }

  return (
    <Section>
      <Header>
        <HeaderTopRow>
          <Title>Move-prep checklist</Title>
          <PercentLabel>{overallPercent}% complete</PercentLabel>
        </HeaderTopRow>
        <ProgressTrack>
          <ProgressFill style={{ width: `${overallPercent}%` }} />
        </ProgressTrack>
        <Subtitle>
          Everything you should knock out before move-in. Tap to check off —
          progress saves automatically.
        </Subtitle>
      </Header>

      <CategoryList>
        {groups.map(group => (
          <CategoryBlock key={group.label}>
            <CategorySummary>
              <CategoryGlyph aria-hidden="true">
                <Icon icon={group.glyph} />
              </CategoryGlyph>
              <CategoryBody>
                <CategoryTitle>{group.label}</CategoryTitle>
                <CategoryHint>{group.blurb}</CategoryHint>
              </CategoryBody>
              <CategoryProgress
                isComplete={
                  group.completedCount === group.tasks.length &&
                  group.tasks.length > 0
                }
              >
                {group.completedCount} / {group.tasks.length}
              </CategoryProgress>
            </CategorySummary>
            <TaskList>
              {group.tasks.map(task => {
                const isChecked = (completions[task.id] ?? 0) > 0

                return (
                  <TaskRow key={task.id} onClick={() => handleToggle(task)}>
                    <TaskCheckbox isChecked={isChecked} aria-hidden="true">
                      {isChecked === true ? <Icon icon={faCheck} /> : ''}
                    </TaskCheckbox>
                    <TaskBody>
                      <TaskTitle isChecked={isChecked}>{task.title}</TaskTitle>
                      <TaskHint>{task.hint}</TaskHint>
                    </TaskBody>
                  </TaskRow>
                )
              })}
            </TaskList>
          </CategoryBlock>
        ))}
      </CategoryList>
    </Section>
  )
}

export default MovePrepChecklist
